import { LoadingButton } from "@mui/lab";
import { Box } from "@mui/material";
import { AxiosError } from "axios";
import { Formik } from "formik";
import { useContext, useState } from "react";
import * as Yup from "yup";
import { ObjectShape } from "yup";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import { ConfigItem as Item } from "../../../app/backendTypes";
import Morselbar from "../Morselbar";
import { ItemApprovals } from "./ItemApprovals";

function ItemFormApproval(props: {
  item: Item;
  kind: string;
  subKind?: string;
}) {
  const { item, kind, subKind } = props;

  const shape: ObjectShape = {};

  if (item.approvals !== undefined) {
    shape.approvals = Yup.object().shape({
      steps: Yup.array().of(
        Yup.object().shape({
          kind: Yup.string().oneOf(["Manager", "Dynamic", "User"]).required(),
          inner: Yup.object().when("kind", {
            is: "User",
            then: (schema) =>
              schema.shape({
                id: Yup.number().not([0]).required(),
              }),
          }),
        })
      ),
    });
  }

  const schema = Yup.object().shape(shape);

  const client = useContext(HttpClientContext);

  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<{
    error?: string;
    content: string;
  }>({ content: "" });

  const handleSubmit = (item: Item) => {
    setLoading(true);
    setOpen(false);
    setMessage({ content: "" });

    const submitItem: Item = structuredClone(item);
    if (submitItem.approvals !== undefined)
      for (const step of submitItem.approvals.steps)
        if (step.kind !== "User") step.inner = undefined;

    const config = {
      kind: {
        kind,
        inner: subKind !== undefined ? { inner: subKind } : undefined,
      },
      item: submitItem,
    };

    client
      ?.post("/api/approvals", config)
      .then(() => {
        setMessage({
          error: undefined,
          content: `${subKind ? subKind : kind} approvals updated`,
        });
        setOpen(true);
      })
      .catch((e: AxiosError) => {
        setMessage({
          error: e.response?.headers[X_REQUEST_ID],
          content: `Failed to update ${subKind ? subKind : kind} approvals`,
        });
        setOpen(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Formik
        initialValues={item}
        onSubmit={(values) => handleSubmit(values)}
        validationSchema={schema}
      >
        {({ values, handleSubmit }) => (
          <Box
            component="form"
            onSubmit={(...args) => {
              handleSubmit(...args);
            }}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {values.approvals !== undefined && (
              <ItemApprovals loading={loading} />
            )}

            <LoadingButton type="submit" loading={loading}>
              Submit
            </LoadingButton>
          </Box>
        )}
      </Formik>

      <Morselbar
        open={open}
        setOpen={setOpen}
        content={message.content}
        error={message.error}
      />
    </>
  );
}

export default ItemFormApproval;
